import React, { createContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Redirect, Route, useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { useAbility } from '@casl/react';
import { Ability, AbilityBuilder } from '@casl/ability';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'providers/AuthenticationProvider';
import PrimeReact from 'primereact/api';
import { AppTopbar } from './AppTopbar';
import { AppFooter } from './AppFooter';
import { AppMenu } from './AppMenu';
import { AppConfig } from './AppConfig';

import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import 'prismjs/themes/prism-coy.css';
import 'layout/flags/flags.css';
import 'layout/layout.scss';
import './App.scss';

import { AbilityContext } from 'configs/CASLContext';
import { SecuredRoute } from 'components/SecuredRoute';
import { findAuthorities } from 'services/AuthService';
import { handleError } from 'utils/handleError';
import { Login } from 'pages/Login';
import { Dashboard } from 'pages/Dashboard';
import { Users } from 'pages/system/Users';
import { Roles } from 'pages/system/Roles';
import { Subjects } from 'pages/system/Subjects';
import { Configs } from 'pages/system/Configs';
import { Clients } from 'pages/integration/Clients';
import { Countries } from 'pages/category/Countries';
import { Units } from 'pages/category/Units';
import { Divisions } from 'pages/category/Divisions';
import { Templates } from 'pages/system/Templates';
import { SmsLogs } from 'pages/log/SmsLogs';
import { EmailLogs } from 'pages/log/EmailLogs';
import { IdpEventLogs } from 'pages/log/IdpEventLogs';
import { IdpAdminEventLogs } from 'pages/log/IdpAdminEventLogs';
import { SecQuestions } from 'pages/system/SecQuestions';
import { Devices } from 'pages/system/Devices';
import { AppVersions } from 'pages/system/AppVersions';
import { Citizens } from 'pages/citizen/Citizens';
import { Registers } from 'pages/citizen/Registers';
import { Foreigners } from 'pages/foreigner/Foreigners';
import { ForeignerRegisters } from 'pages/foreigner/ForeignerRegisters';
import { Parameters } from 'pages/system/Parameters';
import { CitizenOTPs } from 'pages/citizen/CitizenOTPs';
import { ForeignerOTPs } from 'pages/foreigner/ForeignerOTPs';
import { BloodGroups } from 'pages/category/BloodGroups';
import { Genders } from 'pages/category/Genders';
import {
    AccountRegister,
    AppVersion,
    BloodGroup,
    Citizen,
    Client,
    Config,
    Country,
    Device,
    Division,
    EmailLog,
    Foreigner,
    ForeignerRegister,
    Gender,
    IdpAdminEventLog,
    IdpEventLog,
    Role,
    SecQuestion,
    SendOTP,
    SmsLog,
    Subject,
    Template,
    Unit,
    User,
    Parameter,
    Nation,
    Religion,
    Relation,
    MaritalStatus,
    Crime,
    DrivingLicenseClass,
    Education,
    Language,
    Specialize,
    LicensingAgency,
    SysCategory,
    Job,
    AccountRegisterHistory,
    UserTransaction,
    NotificationLog,
    DependentHistory,
    PersonInfo,
    ApprovalResult,
    ProcessingStatus,
    NotificationEvent,
    SendReceptData,
    RequestLookup,
    ForeignerPersonal,
    UserQrcode,
    ProcessStatusForeign,
    ActionJob,
    VNeIDJob,
    DashboardReport,
    Nationality,
    JudicialRecords as JudicialRecord
} from 'utils/constants';
import { Nations } from 'pages/category/Nations';
import { Religions } from 'pages/category/Religions';
import { Nationalities } from 'pages/category/Nationalities';
import { Relations } from 'pages/category/Relations';
import { MaritalStatuses } from 'pages/category/MaritalStatuses';
import { Crimes } from 'pages/category/Crimes';
import { DrivingLicenseClasses } from 'pages/category/DrivingLicenseClasses';
import { Educations } from 'pages/category/Educations';
import { Languages } from 'pages/category/Languages';
import { Specializes } from 'pages/category/Specializes';
import { LicensingAgencies } from 'pages/category/LicensingAgencies';
import { SysCategories } from 'pages/category/SysCategories';
import { Jobs } from 'pages/category/Jobs';
import { AccountRegisterHistories } from 'pages/citizen/AccountRegisterHistories';
import { UserTransactions } from 'pages/system/UserTransactions';
import { NotificationLogs } from 'pages/log/NotificationLogs';
import { DependentHistories } from 'pages/search/DependentHistories';
import { PersonInfos } from 'pages/search/PersonInfos';
import { ApprovalResults } from 'pages/search/ApprovalResults';
import { ProcessingStatuses } from 'pages/search/ProcessingStatuses';
import { NotificationEvents } from 'pages/citizen/NotificationEvents';
import { SendReceptDatas } from 'pages/integration/SendReceptDatas';
import { RequestLookups } from 'pages/integration/RequestLookups';
import { ForeignerPersonals } from 'pages/search/ForeignerPersonals';
import { UserQrcodes } from 'pages/search/UserQrcodes';
import { ProcessStatusForeigns } from 'pages/search/ProcessStatusForeigns';
import { ActionJobs } from 'pages/system/ActionJobs';
import { VNeIDJobs } from 'pages/system/VNeIDJobs';
import { ThongKe } from 'pages/ThongKeSoLieu';
import { JudicialRecords } from 'pages/Log3rdPart/JudicialRecords';
import httpMethod from './services/update2025/httpMethod';
import BaseUrl from './constant/baseUrl';
import XacThuc from './pages/XacThuc';
import { menu, menuPermissions } from 'configs/menuConfig';

export const AppContext = createContext({
    setLoading: () => {},
    loading: false
});

// Tìm đường dẫn của menu đầu tiên (theo thứ tự cấu hình) mà user có quyền truy cập.
// Quy tắc khớp quyền giống AppMenu: chỉ cần subject khớp với một rule là được.
const getFirstAccessiblePath = (ability) => {
    for (const category of menu) {
        for (const item of category.items || []) {
            if (!item.to) continue;
            const permissions = menuPermissions[item.id];
            const allowed =
                permissions?.length === 0 ||
                (permissions?.length > 0 && permissions.some((permission) => ability.rules.some((rule) => rule.subject === permission.subject)));
            if (allowed) return item.to;
        }
    }
    return null;
};

const App = () => {
    const [loading, setLoading] = useState(false);
    const [layoutMode, setLayoutMode] = useState('static');
    const [layoutColorMode, setLayoutColorMode] = useState('light');
    const [inputStyle, setInputStyle] = useState('outlined');
    const [ripple, setRipple] = useState(true);
    const [staticMenuInactive, setStaticMenuInactive] = useState(false);
    const [overlayMenuActive, setOverlayMenuActive] = useState(false);
    const [mobileMenuActive, setMobileMenuActive] = useState(false);
    const [mobileTopbarMenuActive, setMobileTopbarMenuActive] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);

    const toast = useRef(null);
    const { isLogged } = useAuth();
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;
    const ability = useAbility(AbilityContext);
    const location = useLocation();
    const { t, i18n } = useTranslation('menu');

    useEffect(() => {
        // setShowSpinner(true);
        if (!isLogged || !accessToken) {
            return;
        } // chỉ chạy khi đã login

        if (isLogged) {
            let headers = {
                Authorization: `Bearer ${accessToken}`,
                'Accept-Language': i18n.language
            };

            findAuthorities(headers)
                .then((res) => {
                    const authorities = (res.data || []).filter((authority) => authority !== 'ACTIVATED-USERS');

                    const { can, rules } = new AbilityBuilder(Ability);
                    authorities.forEach((authority) => {
                        const array = authority.split(/-(.+)/);
                        let subject = '';
                        switch (array[1]) {
                            case 'USERS':
                                subject = User;
                                break;
                            case 'ROLES':
                                subject = Role;
                                break;
                            case 'SUBJECTS':
                                subject = Subject;
                                break;
                            case 'CONFIGS':
                                subject = Config;
                                break;
                            case 'CLIENTS':
                                subject = Client;
                                break;
                            case 'UNITS':
                                subject = Unit;
                                break;
                            case 'COUNTRIES':
                                subject = Country;
                                break;
                            case 'DIVISIONS':
                                subject = Division;
                                break;
                            case 'TEMPLATES':
                                subject = Template;
                                break;
                            case 'SMS-LOGS':
                                subject = SmsLog;
                                break;
                            case 'EMAIL-LOGS':
                                subject = EmailLog;
                                break;
                            case 'IDP-EVENT-LOGS':
                                subject = IdpEventLog;
                                break;
                            case 'IDP-ADMIN-EVENT-LOGS':
                                subject = IdpAdminEventLog;
                                break;
                            case 'SEC-QUESTIONS':
                                subject = SecQuestion;
                                break;
                            case 'DEVICES':
                                subject = Device;
                                break;
                            case 'APP-VERSIONS':
                                subject = AppVersion;
                                break;
                            case 'CITIZENS':
                                subject = Citizen;
                                break;
                            case 'ACCOUNT-REGISTERS':
                                subject = AccountRegister;
                                break;
                            case 'FOREIGNERS':
                                subject = Foreigner;
                                break;
                            case 'FOREIGNER-REGISTERS':
                                subject = ForeignerRegister;
                                break;
                            case 'PARAMETERS':
                                subject = Parameter;
                                break;
                            case 'SEND-OTP':
                                subject = SendOTP;
                                break;
                            case 'BLOOD-GROUPS':
                                subject = BloodGroup;
                                break;
                            case 'GENDERS':
                                subject = Gender;
                                break;
                            // bổ sung
                            case 'NATIONS':
                                subject = Nation;
                                break;
                            case 'NATIONLITY':
                                subject = Nationality;
                                break;
                            case 'RELIGIONS':
                                subject = Religion;
                                break;
                            case 'RELATIONS':
                                subject = Relation;
                                break;
                            case 'MARITAL-STATUSES':
                                subject = MaritalStatus;
                                break;
                            case 'CRIME':
                                subject = Crime;
                                break;
                            case 'DRIVINGLICENSECLASS':
                                subject = DrivingLicenseClass;
                                break;
                            case 'EDUCATION':
                                subject = Education;
                                break;
                            case 'LANGUAGES':
                                subject = Language;
                                break;
                            case 'SPECIALIZE':
                                subject = Specialize;
                                break;
                            case 'LICENSINGAGENCY':
                                subject = LicensingAgency;
                                break;
                            case 'SYSCATEGORY':
                                subject = SysCategory;
                                break;
                            case 'JOB':
                                subject = Job;
                                break;
                            case 'USER-HISTORY':
                                subject = AccountRegisterHistory;
                                break;
                            case 'USER-TRANSACTIONS':
                                subject = UserTransaction;
                                break;
                            case 'NOTIFICATION-LOGS':
                                subject = NotificationLog;
                                break;
                            case 'CHANGE-DEPENDENT':
                                subject = DependentHistory;
                                break;
                            case 'PERSONAL-INFO':
                                subject = PersonInfo;
                                break;
                            case 'DOCUMENT-APPROVED':
                                subject = ApprovalResult;
                                break;
                            case 'PROCESSING-STATUS':
                                subject = ProcessingStatus;
                                break;
                            case 'NOTIFICATION-EVENT':
                                subject = NotificationEvent;
                                break;
                            case 'SEND-RECEIPT-DATA':
                                subject = SendReceptData;
                                break;
                            case 'REQUEST-LOOKUP':
                                subject = RequestLookup;
                                break;
                            case 'FOREIGNER-INFO':
                                subject = ForeignerPersonal;
                                break;
                            case 'USER-QRCODE':
                                subject = UserQrcode;
                                break;
                            case 'PROCESSING-STATUS-FOREIGN':
                                subject = ProcessStatusForeign;
                                break;
                            case 'ACTION-JOB':
                                subject = ActionJob;
                                break;
                            case 'JOB-MANAGER':
                                subject = VNeIDJob;
                                break;
                            case 'DASHBOARD-REPORT':
                                subject = DashboardReport;
                                break;
                            case 'THONG-KE-SO-LIEU':
                                subject = ThongKe;
                                break;
                            case 'FOREIGNER-OTPS':
                                subject = ForeignerOTPs;
                                break;
                            case 'JUDICIAL-RECORDS':
                                subject = JudicialRecord;
                                break;
                            default:
                                break;
                        }

                        can(String(array[0]).toLowerCase(), subject);
                    });
                    ability.update(rules);
                })
                .catch((error) => handleError(toast, t, error))
                .finally(() => {
                    // setShowSpinner(false);
                });
        }
    }, [isLogged, accessToken, i18n.language]);

    PrimeReact.ripple = true;

    let menuClick = false;
    let mobileTopbarMenuClick = false;

    useEffect(() => {
        if (mobileMenuActive) {
            addClass(document.body, 'body-overflow-hidden');
        } else {
            removeClass(document.body, 'body-overflow-hidden');
        }
    }, [mobileMenuActive]);

    const onInputStyleChange = (inputStyle) => {
        setInputStyle(inputStyle);
    };

    const onRipple = (e) => {
        PrimeReact.ripple = e.value;
        setRipple(e.value);
    };

    const onLayoutModeChange = (mode) => {
        setLayoutMode(mode);
    };

    const onColorModeChange = (mode) => {
        setLayoutColorMode(mode);
    };

    const onWrapperClick = (event) => {
        if (!menuClick) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }

        if (!mobileTopbarMenuClick) {
            setMobileTopbarMenuActive(false);
        }

        mobileTopbarMenuClick = false;
        menuClick = false;
    };

    const onToggleMenuClick = (event) => {
        menuClick = true;

        if (isDesktop()) {
            if (layoutMode === 'overlay') {
                if (mobileMenuActive === true) {
                    setOverlayMenuActive(true);
                }

                setOverlayMenuActive((prevState) => !prevState);
                setMobileMenuActive(false);
            } else if (layoutMode === 'static') {
                setStaticMenuInactive((prevState) => !prevState);
            }
        } else {
            setMobileMenuActive((prevState) => !prevState);
        }

        event.preventDefault();
    };

    const onSidebarClick = () => {
        menuClick = true;
    };

    const onMobileTopbarMenuClick = (event) => {
        mobileTopbarMenuClick = true;

        setMobileTopbarMenuActive((prevState) => !prevState);
        event.preventDefault();
    };

    const onMobileSubTopbarMenuClick = (event) => {
        mobileTopbarMenuClick = true;

        event.preventDefault();
    };

    const onMenuItemClick = (event) => {
        if (!event.item.items) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }
    };
    const isDesktop = () => {
        return window.innerWidth >= 992;
    };

    const addClass = (element, className) => {
        if (element.classList) element.classList.add(className);
        else element.className += ' ' + className;
    };

    const removeClass = (element, className) => {
        if (element.classList) element.classList.remove(className);
        else
            element.className = element.className.replace(
                new RegExp(
                    '(^|\\b)' + className.split(' ').join('|') + '(\\b|$)',
                    'gi'
                ),
                ' '
            );
    };

    const wrapperClass = classNames('layout-wrapper', {
        'layout-overlay': layoutMode === 'overlay',
        'layout-static': layoutMode === 'static',
        'layout-static-sidebar-inactive':
            staticMenuInactive && layoutMode === 'static',
        'layout-overlay-sidebar-active':
            overlayMenuActive && layoutMode === 'overlay',
        'layout-mobile-sidebar-active': mobileMenuActive,
        'p-input-filled': inputStyle === 'filled',
        'p-ripple-disabled': ripple === false,
        'layout-theme-light': layoutColorMode === 'light'
    });

    return (
        <AppContext.Provider value={{ loading, setLoading }}>
            <div className={wrapperClass} onClick={onWrapperClick}>
                {isLogged && (
                    <AppTopbar
                        onToggleMenuClick={onToggleMenuClick}
                        layoutColorMode={layoutColorMode}
                        mobileTopbarMenuActive={mobileTopbarMenuActive}
                        onMobileTopbarMenuClick={onMobileTopbarMenuClick}
                        onMobileSubTopbarMenuClick={onMobileSubTopbarMenuClick}
                    />
                )}

                {isLogged && (
                    <>
                        <div
                            className="layout-sidebar"
                            onClick={onSidebarClick}>
                            <AppMenu
                                onMenuItemClick={onMenuItemClick}
                                layoutColorMode={layoutColorMode}
                            />
                        </div>
                    </>
                )}

                <div
                    className={
                        isLogged
                            ? 'layout-main-container'
                            : 'layout-main-container-without-menu'
                    }>
                    <Toast ref={toast} />
                    <div className="layout-main">
                        <Route path="/xac-thuc" component={XacThuc} />
                        <Route path="/login" component={Login} />
                        {location.pathname === "/" && !isLogged && <Redirect to="/login" />}
                        {location.pathname === "/" && isLogged && ability.rules.length > 0 && <Redirect to={getFirstAccessiblePath(ability) || "/thong-ke-so-lieu"} />}

                        <SecuredRoute
                            path="/thong-ke-so-lieu"
                            component={ThongKe}
                        />
                        <SecuredRoute path="/dashboard" component={Dashboard} />
                        <SecuredRoute path="/users" component={Users} />
                        <SecuredRoute path="/roles" component={Roles} />
                        <SecuredRoute path="/subjects" component={Subjects} />
                        <SecuredRoute path="/configs" component={Configs} />
                        <SecuredRoute path="/clients" component={Clients} />
                        <SecuredRoute path="/countries" component={Countries} />
                        <SecuredRoute path="/units" component={Units} />
                        <SecuredRoute path="/divisions" component={Divisions} />
                        <SecuredRoute path="/templates" component={Templates} />
                        <SecuredRoute path="/sms-logs" component={SmsLogs} />
                        <SecuredRoute
                            path="/email-logs"
                            component={EmailLogs}
                        />
                        <SecuredRoute
                            path="/idp-event-logs"
                            component={IdpEventLogs}
                        />
                        <SecuredRoute
                            path="/idp-admin-event-logs"
                            component={IdpAdminEventLogs}
                        />
                        <SecuredRoute
                            path="/sec-questions"
                            component={SecQuestions}
                        />
                        <SecuredRoute path="/devices" component={Devices} />
                        <SecuredRoute
                            path="/app-versions"
                            component={AppVersions}
                        />
                        <SecuredRoute path="/citizens" component={Citizens} />
                        <SecuredRoute
                            path="/account-registers"
                            component={Registers}
                        />
                        <SecuredRoute
                            path="/foreigners"
                            component={Foreigners}
                        />
                        <SecuredRoute
                            path="/foreigner-registers"
                            component={ForeignerRegisters}
                        />
                        <SecuredRoute
                            path="/parameters"
                            component={Parameters}
                        />
                        <SecuredRoute
                            path="/citizen-otps"
                            component={CitizenOTPs}
                        />
                        <SecuredRoute
                            path="/foreigner-otps"
                            component={ForeignerOTPs}
                        />
                        <SecuredRoute
                            path="/blood-groups"
                            component={BloodGroups}
                        />
                        <SecuredRoute path="/genders" component={Genders} />
                        {/* bổ sung */}
                        <SecuredRoute path="/nations" component={Nations} />
                        <SecuredRoute path="/religions" component={Religions} />
                        <SecuredRoute
                            path="/nationalities"
                            component={Nationalities}
                        />
                        <SecuredRoute path="/relations" component={Relations} />
                        <SecuredRoute
                            path="/marital-status"
                            component={MaritalStatuses}
                        />
                        <SecuredRoute path="/crimes" component={Crimes} />
                        <SecuredRoute
                            path="/driving-license-classes"
                            component={DrivingLicenseClasses}
                        />
                        <SecuredRoute
                            path="/educations"
                            component={Educations}
                        />
                        <SecuredRoute path="/languages" component={Languages} />
                        <SecuredRoute
                            path="/specializes"
                            component={Specializes}
                        />
                        <SecuredRoute
                            path="/licensingAgencies"
                            component={LicensingAgencies}
                        />
                        <SecuredRoute
                            path="/sysCategories"
                            component={SysCategories}
                        />
                        <SecuredRoute path="/jobs" component={Jobs} />
                        <SecuredRoute
                            path="/account-registers-history"
                            component={AccountRegisterHistories}
                        />
                        <SecuredRoute
                            path="/user-transactions"
                            component={UserTransactions}
                        />
                        <SecuredRoute
                            path="/notification-logs"
                            component={NotificationLogs}
                        />
                        <SecuredRoute
                            path="/dependent-histories"
                            component={DependentHistories}
                        />
                        <SecuredRoute
                            path="/personal-info"
                            component={PersonInfos}
                        />
                        <SecuredRoute
                            path="/approval-results"
                            component={ApprovalResults}
                        />
                        <SecuredRoute
                            path="/processing-status"
                            component={ProcessingStatuses}
                        />
                        <SecuredRoute
                            path="/notification-events"
                            component={NotificationEvents}
                        />
                        <SecuredRoute
                            path="/send-recept-data"
                            component={SendReceptDatas}
                        />
                        <SecuredRoute
                            path="/request-lookup"
                            component={RequestLookups}
                        />
                        <SecuredRoute
                            path="/foreigner-personal"
                            component={ForeignerPersonals}
                        />
                        <SecuredRoute
                            path="/user-qrcode"
                            component={UserQrcodes}
                        />
                        <SecuredRoute
                            path="/process-status-foreign"
                            component={ProcessStatusForeigns}
                        />
                        <SecuredRoute
                            path="/action-job"
                            component={ActionJobs}
                        />
                        <SecuredRoute path="/vneid-job" component={VNeIDJobs} />

                        <SecuredRoute
                            path="/judicial-records"
                            component={JudicialRecords}
                        />
                    </div>
                    {isLogged && (
                        <AppFooter layoutColorMode={layoutColorMode} />
                    )}
                </div>

                {isLogged && (
                    <AppConfig
                        rippleEffect={ripple}
                        onRippleEffect={onRipple}
                        inputStyle={inputStyle}
                        onInputStyleChange={onInputStyleChange}
                        layoutMode={layoutMode}
                        onLayoutModeChange={onLayoutModeChange}
                        layoutColorMode={layoutColorMode}
                        onColorModeChange={onColorModeChange}
                    />
                )}

                <CSSTransition
                    classNames="layout-mask"
                    timeout={{ enter: 200, exit: 200 }}
                    in={mobileMenuActive}
                    unmountOnExit>
                    <div className="layout-mask p-component-overlay" />
                </CSSTransition>

                <Dialog
                    visible={!isLogged}
                    closable={false}
                    resizable={false}
                    draggable={false}
                    showHeader={false}>
                    <ProgressSpinner />
                </Dialog>
            </div>
        </AppContext.Provider>
    );
};

export default App;
